import { useEffect, useState } from "react";
import {
  billingAddress,
  capitalize,
  currency,
  vatLines,
} from "../../../services/utilities";
import { Privileges } from "../../../services/fakeDb";
import Header from "./header";
import Body from "./body";
import Footer from "./footer";

const Hr = ({ className = "" }) => (
  <hr
    style={{
      border: "none",
      borderTop: "1px dashed #000",
      height: 0,
    }}
    className={`my-1 ${className}`}
  />
);

const careOfName = (careOf) => {
  const { user = {} } = careOf;
  const { fullName = {} } = user || {};
  return `${fullName.fname} ${fullName?.lname[0]?.toUpperCase()}.`;
};

const Text = ({ title = "", value = "", className = "", fontSize = "" }) => {
  return (
    <div className={`d-flex justify-content-between ${className}`}>
      <span className="mr-2">{title}:</span>
      <span
        style={{
          fontSize,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          maxWidth: "100%", // or any fixed width like '200px'
        }}
      >
        {value}
      </span>
    </div>
  );
};

const handleMixedLabel = ({ refNo }) => {
  const { pp = "cash", amount = 0, careOf = {} } = refNo || {};
  const split1IsCash = pp === "cash";
  const split2IsGCash = careOf.pp === "gcash";

  return (
    <>
      <Text
        title={capitalize(split1IsCash ? "Cash" : "Gcash")}
        value={currency.format(amount)}
      />
      <Hr />
      <Text
        title={split2IsGCash ? "GCash" : "C/O"}
        value={
          split2IsGCash
            ? currency.format(careOf.amount)
            : `${careOfName(careOf)} (${currency.format(careOf.amount)})`
        }
      />
    </>
  );
};

const Stub = ({ sale }) => {
  const {
      _id,
      createdAt = "",
      payment = 0,
      customer = {},
      privilege,
      amount = 0,
      cash = 0,
      discount = 0,
      vat = 0,
      lessDiscount = 0,
      cashier = {},
      cart = [],
      refNo = {},
      cardHolder = {},
      companyId,
    } = sale,
    {
      fullName = {},
      address = {},
      email = sale.customerId?.email || "",
    } = customer || {};
  const { careOf = {}, pp = "cash" } = refNo || {};
  const isVoucher = payment === "voucher";
  const isMixed = payment === "mixed";
  const cashOut = amount - refNo?.amount || 0;
  const hasCashOut = cashOut > 0 && isVoucher && pp === "cash";
  const cardHolderProvider =
    cardHolder?.provider ||
    (cardHolder?.type === "wls"
      ? cardHolder?.company?.name || ""
      : cardHolder?.type || "");
  const cardHolderType =
    cardHolder?.type ||
    (cardHolderProvider === "phi"
      ? "phi"
      : ["mbs", "ctr"].includes(cardHolderProvider)
        ? cardHolderProvider
        : cardHolderProvider
          ? "wls"
          : "");

  const isCardHolder = Boolean(
    cardHolderProvider ||
      cardHolder?.company?.name ||
      cardHolder?.company?.ref
  );

  const change = hasCashOut ? cash - cashOut : cash - amount;
  const lines = vatLines(sale);
  // PWD / Senior / Indigenous sale with items that got no discount
  const hasFixed = vat > 0 && lines.fixed > 0;

  return (
    <div
      style={{
        width: "105mm",
        lineHeight: "20px",
        cursor: "default",
        fontFamily: "Courier New, monospace",
        letterSpacing: "-0.5px",
        fontSize: "20px",
        wordSpacing: "-1px",
      }}
      className="text-center thermal-font"
    >
      <Header date={createdAt} dealId={_id} />
      <Text
        className="mt-2"
        title="Name"
        value={String(
          `${fullName.fname || ""} ${
            String(`${fullName.mname.charAt(0)}. `) || ""
          }  ${fullName.lname || ""}`
        ).toUpperCase()}
      />

      <Text
        title="Address"
        value={billingAddress(address)}
        isAddress
        fontSize="0.9rem"
      />
      {privilege !== 0 && (
        <Text title="Privilege" value={Privileges[privilege] || "-"} />
      )}
      <Hr />
      <Body cart={cart} markFixed={hasFixed} />
      <Hr />
      {hasFixed ? (
        // discountable services only, the promo ones are added after
        <Text title="Subtotal" value={currency.format(lines.discountable)} />
      ) : (
        <Text
          title="Total"
          value={currency.format(amount + discount)}
          className="fw-bold"
        />
      )}
      {vat > 0 ? (
        // PWD / Senior / Indigenous, same lines as the service invoice
        <>
          <Text title="Less VAT" value={currency.format(lines.vat)} />
          <Text title="Net of VAT" value={currency.format(lines.vatExempt)} />
          <Text
            title={hasFixed ? "Less Discount (20%)" : "Less Discount"}
            value={currency.format(lessDiscount)}
          />
          <Text title="Add VAT" value={currency.format(lines.vat)} />
          {hasFixed && (
            <>
              <Hr />
              <Text
                title="Net Price"
                value={currency.format(lines.discounted)}
              />
              <Text title="Promo" value={currency.format(lines.fixed)} />
              <Hr />
            </>
          )}
          <Text
            title={hasFixed ? "AMOUNT DUE" : "Amount Due"}
            value={currency.format(amount)}
            className="fw-bold"
          />
        </>
      ) : (
        discount > 0 && (
          <>
            <Text title="Discount" value={currency.format(discount)} />
            <Text
              title="Subtotal"
              value={currency.format(amount)}
              className="fw-bold"
            />
          </>
        )
      )}
      <Hr />
      {hasCashOut && (
        <>
          <Text title={"Voucher"} value={currency.format(refNo.amount)} />
          <Text title={"Patient Share"} value={currency.format(cashOut)} />
        </>
      )}
      {isMixed ? (
        handleMixedLabel(sale)
      ) : (
        <Text
          title={capitalize(
            payment === "cash" || hasCashOut ? "Tendered" : payment
          )}
          value={
            payment === "cash" || hasCashOut
              ? currency.format(cash)
              : currency.format(refNo.amount)
          }
        />
      )}
      {(payment === "cash" || hasCashOut) && change > 0 && (
        <Text title="Change" value={currency.format(change)} />
      )}
      {isVoucher && (
        <>
          {isCardHolder && (
            <>
              <Hr />
              <Text
                title={cardHolderType === "wls" ? "Card No." : "Tracking No."}
                value={`${refNo.number}`}
              />
            </>
          )}
          {careOf?.user?._id && (
            <>
              <Hr />
              <Text
                title="C/O"
                value={`${careOfName(careOf)} (${currency.format(
                  careOf.amount
                )})`}
              />
            </>
          )}
        </>
      )}
      <Hr />
      <Text
        title="Cashier"
        value={capitalize(
          `${cashier?.fname?.split?.(" ")[0] || ""} ${cashier?.lname || ""}`
        )}
      />
      <Hr />
      {hasFixed && (
        <div className="text-left" style={{ fontSize: "15px" }}>
          Legend: ** = promo, non-discountable
        </div>
      )}
      <br />
      <Footer
        email={email}
        companyId={companyId}
        _id={_id}
      />
    </div>
  );
};

export default function ClaimStub() {
  const [sale, setSale] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem("claimStub");
      if (raw) {
        const parsed = JSON.parse(raw);
        setSale(parsed);
      }
      setTimeout(() => {
        window.print();
      }, 500);
    } catch (error) {
      console.error("Failed to parse claimStub:", error);
    }
  }, []);

  if (!sale || !sale?._id) return <div>Sale is Empty</div>;
  return (
    <>
      <Stub sale={sale} />
    </>
  );
}
